import React from "react";
import { Link } from "react-router-dom";
import { MDBBtn } from "mdb-react-ui-kit";
import { availableMarketplaces, upcomingMarketplaces } from "./constants";
import {
  getEbayStoreId,
  handleEbayIntegrate,
  isValidEbayStoreId,
} from "./helpers";
import PoshmarkDetails from "./PoshmarkDetails";
import VintedDetails from "./VintedDetails";

const MarketplacesStep = ({
  formData,
  processingImages,
  progress,
  ebayConnected,
  checkingEbay,
  ebayStores = [],
  handleEbayStoreChange,
  poshmarkConnected,
  checkingPoshmark,
  poshmarkSubCategories,
  vintedConnected,
  checkingVinted,
  vintedMainCategories,
  vintedSubCategories,
  vintedCategoryHistory,
  vintedCurrentCategoryId,
  vintedSelectedCategoryPath,
  vintedLoadingCategories,
  handleMarketplaceToggle,
  handlePoshmarkIntegrate,
  handlePoshmarkCategoryChange,
  handleVintedIntegrate,
  handleVintedCategoryClick,
  handleVintedBack,
  resetVintedCategory,
  handleInputChange,
  handleVariantChange,
  vintedDescription,
  onVintedDescriptionChange,
  showEbay = false,
}) => {
  const isEbaySelected = formData.selectedMarketplaces.includes("ebay");

  return (
    <div className="step-panel">
      {processingImages && (
        <div className="progress-bar-wrapper my-3">
          <div
            className="progress-bar-bg"
            style={{
              width: "100%",
              height: "6px",
              background: "#eee",
              borderRadius: "4px",
            }}
          >
            <div
              className="progress-bar-fill"
              style={{
                width: `${progress}%`,
                height: "100%",
                background: "#6a1b9a",
                borderRadius: "4px",
                transition: "width 0.3s",
              }}
            ></div>
          </div>
          <span style={{ fontSize: "14px", color: "#555" }}>
            {progress}% Processing Images...
          </span>
        </div>
      )}
      <div className="step-header">
        <h2>Select Marketplaces</h2>
        <p>Choose where you want to list your product</p>
      </div>
      <div className="marketplace-grid">
        {availableMarketplaces.map((marketplace) => (
          <div
            key={marketplace.id}
            className={`marketplace-card ${
              formData.selectedMarketplaces.includes(marketplace.id)
                ? "selected"
                : ""
            }`}
          >
            <div
              className="marketplace-select-area"
              onClick={() => handleMarketplaceToggle(marketplace.id)}
            >
              <div className="marketplace-logo">
                <img src={marketplace.logo} alt={marketplace.name} />
              </div>
              <h4>{marketplace.name}</h4>
              <div className="marketplace-check">
                {formData.selectedMarketplaces.includes(marketplace.id)
                  ? "✓"
                  : ""}
              </div>
            </div>
            {marketplace.id === "ebay" && (
              <div className="integration-status ms-2">
                {checkingEbay ? (
                  <small className="text-muted">Checking...</small>
                ) : ebayConnected ? (
                  <small className="text-success">Connected</small>
                ) : (
                  <small className="text-danger">Not connected</small>
                )}
              </div>
            )}
            {marketplace.id === "poshmark" && (
              <MDBBtn
                size="sm"
                color="light"
                className="integrate-btn"
                onClick={handlePoshmarkIntegrate}
                disabled={checkingPoshmark}
              >
                {checkingPoshmark
                  ? "Checking..."
                  : poshmarkConnected
                    ? "Connected"
                    : "Integrate"}
              </MDBBtn>
            )}
            {marketplace.id === "vinted" && (
              <MDBBtn
                size="sm"
                color="light"
                className="integrate-btn"
                onClick={handleVintedIntegrate}
                disabled={checkingVinted}
              >
                {checkingVinted
                  ? "Checking..."
                  : vintedConnected
                    ? "Connected"
                    : "Integrate"}
              </MDBBtn>
            )}
          </div>
        ))}
        {upcomingMarketplaces.map((marketplace) => (
          <div key={marketplace.id} className="marketplace-card upcoming">
            <div className="marketplace-logo">
              <img src={marketplace.logo} alt={`${marketplace.name} logo`} />
            </div>
            <h4>{marketplace.name}</h4>
            <div className="marketplace-badge">Coming Soon</div>
          </div>
        ))}
      </div>

      {isEbaySelected && (
        <div className="ebay-store-selector mt-4">
          {showEbay ? (
            // Editor ko full store selection dikhe
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h4 className="section-title mb-1">Select eBay Store</h4>
                  <p className="text-muted mb-0">
                    Choose which connected eBay store to publish this listing
                    to.
                  </p>
                </div>
                <MDBBtn
                  size="sm"
                  color="light"
                  className="integrate-btn"
                  onClick={handleEbayIntegrate}
                >
                  + Add Store
                </MDBBtn>
              </div>

              {checkingEbay ? (
                <p className="text-muted">Loading eBay stores...</p>
              ) : ebayStores.length === 0 ? (
                <div className="ebay-store-empty">
                  <p className="text-danger mb-2">
                    No eBay store connected for this company.
                  </p>
                  <Link to="/integrations/ebay">Go to eBay Integration</Link>
                </div>
              ) : (
                <div className="ebay-store-options">
                  {ebayStores.map((store) => {
                    const storeId = getEbayStoreId(store);
                    if (!isValidEbayStoreId(storeId)) return null;
                    const isSelected =
                      String(formData.selectedEbayStoreId) === String(storeId);
                    return (
                      <label
                        key={storeId}
                        className={`ebay-store-option ${isSelected ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="ebayStore"
                          value={storeId}
                          checked={isSelected}
                          onChange={() => handleEbayStoreChange(storeId)}
                        />
                        <img
                          src={
                            store.imageurl ||
                            "https://upload.wikimedia.org/wikipedia/commons/1/1b/EBay_logo.svg"
                          }
                          alt={store.storeName || "eBay store"}
                          className="ebay-store-logo"
                        />
                        <div className="ebay-store-details">
                          <strong>{store.storeName || "eBay Store"}</strong>
                          <small>
                            Marketplace: {store.marketplace || "N/A"}
                          </small>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            // Non-editor ko sirf connected message dikhe
            <div className="mt-3">
              <p className="text-success mb-0">
                <strong>✓ eBay Connected</strong> — Store will be assigned
                automatically.
              </p>
            </div>
          )}
        </div>
      )}
      {formData.selectedMarketplaces.includes("poshmark") && (
        <PoshmarkDetails
          formData={formData}
          poshmarkConnected={poshmarkConnected}
          poshmarkSubCategories={poshmarkSubCategories}
          handlePoshmarkCategoryChange={handlePoshmarkCategoryChange}
          handleInputChange={handleInputChange}
          handleVariantChange={handleVariantChange}
        />
      )}
      {formData.selectedMarketplaces.includes("vinted") && (
        <VintedDetails
          formData={formData}
          vintedConnected={vintedConnected}
          vintedMainCategories={vintedMainCategories}
          vintedSubCategories={vintedSubCategories}
          vintedCategoryHistory={vintedCategoryHistory}
          vintedCurrentCategoryId={vintedCurrentCategoryId}
          vintedSelectedCategoryPath={vintedSelectedCategoryPath}
          vintedLoadingCategories={vintedLoadingCategories}
          handleVintedCategoryClick={handleVintedCategoryClick}
          handleVintedBack={handleVintedBack}
          resetVintedCategory={resetVintedCategory}
          handleInputChange={handleInputChange}
          // ✅ Pass custom description props down
          vintedDescription={vintedDescription}
          onVintedDescriptionChange={onVintedDescriptionChange}
        />
      )}
    </div>
  );
};

export default MarketplacesStep;
